import { Card, Segmented, toast } from '@insight-health-ai/roseate/components';
import {
  Attachment02,
  FileQuestion02,
  XClose,
} from '@insight-health-ai/roseate/icons';
import { Form, Input, Modal, Select, Upload, Button, UploadFile } from 'antd';
import { useState } from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useGetSpecialityList } from 'src/hooks/speciality';
import { useAutoCreateTags } from 'src/hooks/tag';

type AutoCreateTagsModalProps = {
  open: boolean;
  onClose: (success?: boolean) => void;
};

export const AutoCreateTagsModal = ({
  open,
  onClose,
}: AutoCreateTagsModalProps) => {
  const [creationType, setCreationType] = useState<'criteria' | 'specialty'>(
    'specialty'
  );
  const [documents, setDocuments] = useState<UploadFile[]>([]);

  const { mutateAsync: autoCreateTags, isPending } = useAutoCreateTags();
  const { data: { specialties = [] } = {}, isLoading: isLoadingSpecialties } =
    useGetSpecialityList();

  const [form] = Form.useForm();

  const allowDocumentUpload = creationType === 'criteria';

  const handleAutoCreateTags = async () => {
    try {
      const { criteria, specialty } = await form.validateFields();

      const documentDataUrls = await Promise.all(
        documents.map((document) => {
          return new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(reader.error);
            if (document.originFileObj) {
              reader.readAsDataURL(document.originFileObj as Blob);
            } else {
              resolve('');
            }
          });
        })
      );

      let payload = {};
      if (creationType === 'criteria') {
        payload = {
          criteria,
        };
      } else {
        payload = {
          specialty_id: specialty,
        };
      }
      if (documents.length && allowDocumentUpload) {
        payload = {
          ...payload,
          documents: documentDataUrls.filter(Boolean),
        };
      }
      await autoCreateTags(payload);
      onClose?.(true);
    } catch (error) {
      // do nothing
    }
  };

  return (
    <Modal
      open={open}
      width={700}
      closable={!isPending}
      onCancel={() => onClose?.(false)}
      title="Auto Create Tags"
      footer={
        <div className="flex justify-end items-center gap-2 mt-8">
          {isPending && (
            <div className="flex text-left gap-1 items-center text-sm text-gray-500 left-0 mr-auto">
              <InfoCircleOutlined />
              This may take a minute or two while we run our brains.
            </div>
          )}
          <Button
            disabled={isPending}
            className="ih-button-secondary"
            onClick={() => onClose?.(false)}
          >
            Cancel
          </Button>
          <Button
            loading={isPending}
            disabled={isPending}
            className="ih-button-primary"
            onClick={handleAutoCreateTags}
          >
            Auto Create
          </Button>
        </div>
      }
    >
      <Segmented
        options={[
          { label: 'From Specialty', value: 'specialty' },
          { label: 'From Criteria', value: 'criteria' },
        ]}
        value={creationType}
        onChange={(value) => setCreationType(value as 'criteria' | 'specialty')}
      />
      <Form layout="vertical" form={form} className="mt-4">
        {creationType === 'criteria' ? (
          <Form.Item
            key="criteria"
            className="field-wrapper"
            name="criteria"
            label={<span className="label">Criteria</span>}
            rules={[
              {
                required: !documents.length,
                message:
                  'Either describe the criteria or attach documents to proceed',
              },
            ]}
          >
            <Input.TextArea
              className="field"
              autoSize={{ minRows: 10, maxRows: 15 }}
              placeholder="Enter criteria"
            />
          </Form.Item>
        ) : (
          <Form.Item
            key="specialty"
            className="field-wrapper"
            name="specialty"
            label={<span className="label">Specialty</span>}
            rules={[{ required: true, message: 'Specialty is required' }]}
          >
            <Select
              placeholder="Select specialty"
              loading={isLoadingSpecialties}
              className="field"
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={specialties.map((specialty) => ({
                label: specialty.specialty_name,
                value: specialty.specialty_id,
              }))}
            />
          </Form.Item>
        )}
      </Form>
      {allowDocumentUpload && (
        <div className="mt-4 flex flex-col gap-2">
          {documents.map((document) => (
            <Card key={document.uid} padding="none" className="p-2">
              <div className="flex items-center gap-2 justify-between">
                <div className="flex items-center gap-2">
                  <FileQuestion02 className="text-lg" />
                  <div className="text-medium">{document.name}</div>
                </div>
                <Button
                  type="text"
                  icon={<XClose />}
                  className="text-medium"
                  onClick={() =>
                    setDocuments(
                      documents.filter((d) => d.uid !== document.uid)
                    )
                  }
                />
              </div>
            </Card>
          ))}
          <Upload
            fileList={[]}
            multiple
            beforeUpload={() => false}
            accept=".pdf,.png,.jpg,.jpeg"
            onChange={(info) => {
              const validList: UploadFile[] = [];
              const invalidList: UploadFile[] = [];

              info.fileList.forEach((file) => {
                const similarDocument = documents.find(
                  (document) =>
                    document.name === file.name && document.size === file.size
                );
                if (similarDocument) {
                  invalidList.push(file);
                } else {
                  validList.push(file);
                }
              });

              setDocuments((prev) => [...prev, ...validList]);

              if (invalidList.length > 0) {
                toast.warn(
                  <div className="flex flex-col gap-1">
                    <div className="font-semibold text-lg">
                      Duplication Detected
                    </div>
                    <div className="text-sm text-gray-500">
                      These documents are already attached and were not
                      re-added:
                      {invalidList.map((file) => (
                        <div
                          key={file.uid}
                          className="flex items-center gap-2 text-medium"
                        >
                          <FileQuestion02 />
                          {file.name}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }

              form.validateFields();
            }}
          >
            <Button className="ih-button-primary" icon={<Attachment02 />}>
              Add Documents
            </Button>
          </Upload>
        </div>
      )}
    </Modal>
  );
};
